import { useState, useContext, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import Header from "../layout/Header";
import Footer from "../layout/Footer";
import Loader from "../layout/Loader";
import Alert from "../Alert";
import { alertContext } from "../contexts/AlertProvider";
import PermissionGate from "../layout/PermissionGate";
import normalize from "../utils/NormalizeSelect";

const alertTypeOptions = [
    { value: "fixed", label: "Fixed Value" },
    {
        value: "percentage",
        label: "Percentage of Balance After Latest Recharge",
    },
];

const LowBalanceAlert = () => {
    const [data, setData] = useState({
        enabled: false,
        type: "fixed",
        value: "",
    });
    const [error, setError] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [_, setAlertConfig] = useContext(alertContext);
    const token = localStorage.getItem("token");

    const fetchSetting = async () => {
        setIsLoading(true);
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_ENDPOINT}settings/get?key=low-balance-alert`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                },
            );

            if (res.data.code === 1) {
                const value = res?.data?.data?.value || {};
                setData({
                    enabled: value?.enabled ?? false,
                    type: value?.type ?? "fixed",
                    value:
                        value?.value !== undefined && value?.value !== null && value?.value !== 0
                            ? String(value.value)
                            : "",
                });
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const updateSetting = async () => {
        const errors = {};

        if (data.enabled) {
            const valueNum = Number(data.value);
            if (data.value === "" || !Number.isFinite(valueNum) || valueNum <= 0) {
                errors["value"] = "Please enter a valid value";
            }
            if (data.type === "percentage" && valueNum > 100) {
                errors["value"] = "Percentage cannot be more than 100";
            }
        }

        setError(errors);
        if (Object.keys(errors).length > 0) return;

        setIsUpdating(true);

        const payloadValue = {
            enabled: data.enabled,
            type: data.enabled ? data.type : "fixed",
            value: data.enabled ? Number(data.value) : 0,
        };

        try {
            const res = await axios.patch(
                `${process.env.REACT_APP_API_ENDPOINT}settings/patch?key=low-balance-alert`,
                { key: "low-balance-alert", value: payloadValue },
                {
                    headers: { Authorization: `Bearer ${token}` },
                },
            );

            setAlertConfig({
                show: true,
                message: res.data.message,
                type: res.data.code === 1 ? "success" : "danger",
            });

            if (res.data.code === 1) {
                fetchSetting();
            }
        } catch (err) {
            console.error(err);
            setAlertConfig({
                show: true,
                message: "Something went wrong",
                type: "danger",
            });
        } finally {
            setIsUpdating(false);
        }
    };

    useEffect(() => {
        fetchSetting();
    }, []);

    return isLoading === true ? (
        <Loader />
    ) : (
        <div className="d-flex flex-column flex-root">
            <Alert />
            {/*begin::Page*/}
            <div className="page d-flex flex-row flex-column-fluid">
                {/*begin::Wrapper*/}
                <div
                    className="wrapper d-flex flex-column flex-row-fluid"
                    id="kt_wrapper"
                >
                    <Header />
                    <div className="app-body">
                        <div className="toolbar py-3 py-lg-6" id="kt_toolbar">
                            {/*begin::Container*/}
                            <div
                                id="kt_toolbar_container"
                                className=" container-xxl  d-flex flex-stack flex-wrap gap-2"
                            >
                                {/*begin::Page title*/}
                                <div className="page-title d-flex flex-column align-items-start me-3 py-2 py-lg-0 gap-2">
                                    {/*begin::Title*/}
                                    <h1 className="d-flex text-dark fw-bold m-0 fs-3">
                                        Low Balance Alert
                                    </h1>
                                    {/*end::Title*/}
                                </div>
                                {/*end::Page title*/}
                            </div>
                            {/*end::Container*/}
                        </div>
                        {/*end::Toolbar*/}

                        {/*begin::Container*/}
                        <div
                            id="kt_content_container"
                            className="d-flex flex-column-fluid align-items-start  container-xxl "
                        >
                            {/*begin::Post*/}
                            <div className="content flex-row-fluid" id="kt_content">
                                <form autoComplete="off" id="lowBalanceAlertForm">
                                    <div className="row g-5 g-lg-10 mb-5">
                                        {/*begin::Card*/}
                                        <PermissionGate required={`view_low_balance_alert_setting`}>
                                            <div className="card">
                                                {/*begin::Card header*/}
                                                <div className="card-header">
                                                    {/*begin::Card title*/}
                                                    <div className="card-title">
                                                        Merchant Low Balance Alert
                                                    </div>
                                                    {/*begin::Card title*/}
                                                </div>
                                                {/*end::Card header*/}

                                                {/*begin::Card body*/}
                                                <div className="card-body py-4">
                                                    <div className="d-flex justify-content-between align-items-center mb-5">
                                                        <div>
                                                            <h5 className="mb-1 fw-bold">
                                                                Enable Low Balance Alert
                                                            </h5>
                                                            <p className="text-muted mb-0">
                                                                Applies to all merchants. Sends an email to
                                                                each merchant's registered contact email(s)
                                                                whenever their wallet balance drops to the
                                                                configured level.
                                                            </p>
                                                        </div>

                                                        <div className="form-check form-switch form-check-custom form-check-solid">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                id="lowBalanceAlertEnabled"
                                                                checked={data.enabled}
                                                                onChange={(e) =>
                                                                    setData({ ...data, enabled: e.target.checked })
                                                                }
                                                            />
                                                        </div>
                                                    </div>

                                                    {data.enabled && (
                                                        <div className="row">
                                                            <div className="col-md-6 mb-3 fv-row">
                                                                <label className="form-label required">
                                                                    Alert Type
                                                                </label>
                                                                <Select
                                                                    className="react-select-styled react-select-bordered"
                                                                    classNamePrefix="react-select"
                                                                    value={
                                                                        alertTypeOptions.find(
                                                                            (option) => option.value === data.type,
                                                                        ) ?? null
                                                                    }
                                                                    onChange={(e) => {
                                                                        setData({ ...data, type: e?.value ?? "fixed" });
                                                                        setError({ ...error, value: "" });
                                                                    }}
                                                                    options={alertTypeOptions}
                                                                    filterOption={(option, input) => {
                                                                        return normalize(option.label).includes(
                                                                            normalize(input),
                                                                        );
                                                                    }}
                                                                    placeholder="Select an option"
                                                                />
                                                            </div>

                                                            <div className="col-md-6 mb-3 fv-row">
                                                                <label className="form-label required">
                                                                    {data.type === "percentage"
                                                                        ? "Percentage (%)"
                                                                        : "Amount"}
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    autoComplete="off"
                                                                    placeholder={
                                                                        data.type === "percentage" ? "e.g. 10" : "e.g. 50"
                                                                    }
                                                                    value={data.value}
                                                                    onChange={(e) => {
                                                                        const v = e.target.value;
                                                                        if (/^\d*\.?\d*$/.test(v)) {
                                                                            setData({ ...data, value: v });
                                                                            setError({ ...error, value: "" });
                                                                        }
                                                                    }}
                                                                />
                                                                {error.value && (
                                                                    <small className="text-danger mt-2">
                                                                        {error.value}
                                                                    </small>
                                                                )}
                                                            </div>

                                                            <div className="col-12">
                                                                <small className="text-muted">
                                                                    {data.type === "percentage"
                                                                        ? "For each merchant, the threshold is a percentage of that merchant's total wallet balance right after their most recent top-up — not the top-up amount itself."
                                                                        : "The same fixed amount is used as the threshold for every merchant."}
                                                                    {" "}The alert email is sent to the email
                                                                    address(es) saved under that merchant's{" "}
                                                                    <b>Contacts</b> section (Merchant Details
                                                                    page) — make sure each merchant has at
                                                                    least one contact with a valid email.
                                                                </small>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                {/*end::Card body*/}
                                                <PermissionGate
                                                    required={`edit_low_balance_alert_setting`}
                                                    fallback={<></>}
                                                >
                                                    <div className="card-footer">
                                                        <button
                                                            type="button"
                                                            className="btn btn-primary"
                                                            onClick={updateSetting}
                                                            disabled={isUpdating}
                                                            style={{ minWidth: "160px" }}
                                                        >
                                                            {isUpdating ? (
                                                                <>
                                                                    Please wait...
                                                                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                                                </>
                                                            ) : (
                                                                "Update Setting"
                                                            )}
                                                        </button>
                                                    </div>
                                                </PermissionGate>
                                            </div>
                                        </PermissionGate>
                                        {/*end::Card*/}
                                    </div>
                                </form>
                            </div>
                            {/*end::Post*/}
                        </div>
                    </div>
                    <Footer />
                </div>
                {/*end::Wrapper*/}
            </div>
            {/*end::Page*/}
        </div>
    );
};

export default LowBalanceAlert;